import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { documentationApi } from "../../../services/api"
import MermaidDiagram from "../../../components/MermaidDiagram"
import Button from "../../../components/ui/Button"
import Card, { CardBody } from "../../../components/ui/Card"
import { Field, Input, Textarea } from "../../../components/ui/Field"

function renderContentWithDiagrams (text, diagrams) {
  const safeText = String(text || '')
  const lines = safeText.split('\n')
  const safeDiagrams = Array.isArray(diagrams) ? diagrams : []
  let di = 0

  const elements = []
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = String(line || '').trim()

    // If this line in the generated content is a template diagram heading, render the next diagram here.
    if (/^(Diagram|Figure)\b/i.test(trimmed)) {
      const d = safeDiagrams[di]
      if (!d?.code) {
        // If the backend didn't generate a diagram for this heading, keep the line as plain text.
        elements.push(
          <span key={`line-${i}`}>
            {line}
            {i < lines.length - 1 ? <br /> : null}
          </span>
        )
        continue
      }

      elements.push(
        <div key={`diag-${i}-${di}`} className="mt-6 print:hidden">
          <h3 className="text-base font-semibold mb-2 text-slate-800">{d?.name || trimmed}</h3>
          <MermaidDiagram code={d.code} />
        </div>
      )
      di++
      continue
    }

    elements.push(
      <span key={`line-${i}`}>
        {line}
        {i < lines.length - 1 ? <br /> : null}
      </span>
    )
  }

  // Fallback: if the model didn't keep diagram headings, still render any remaining diagrams after the content.
  while (di < safeDiagrams.length) {
    const d = safeDiagrams[di]
    elements.push(
      <div key={`fallback-diag-${di}`} className="mt-6 print:hidden">
        <h3 className="text-base font-semibold mb-2 text-slate-800">{d?.name || `Diagram ${di + 1}`}</h3>
        {d?.code ? <MermaidDiagram code={d.code} /> : null}
      </div>
    )
    di++
  }

  return elements
}

const SingleChapter = ({ setPage, doc, chapter, onDocUpdate }) => {
  const navigate = useNavigate()
  const [editModal, setEditModal] = useState(false)
  const [content, setContent] = useState(chapter?.content ?? '')
  const [aiInstruction, setAiInstruction] = useState('')
  const [saving, setSaving] = useState(false)
  const [regenLoading, setRegenLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setContent(chapter?.content ?? '')
  }, [chapter?._id, chapter?.content])

  if (!doc || !chapter) {
    return (
      <div className="p-4">
        <Button size="sm" variant="outline" onClick={() => setPage('Project')}>Back to Project</Button>
        <p className="mt-2 text-slate-600">No chapter selected.</p>
      </div>
    )
  }

  const maxEdits = 100
  const editCount = doc.editCount ?? 0
  const regenCount = doc.regenerationCount ?? 0
  const canEdit = editCount < maxEdits
  const canRegen = regenCount < 3

  const handleRegenerate = async () => {
    if (!canRegen) {
      setError('Regeneration limit (3) reached. Purchase more to regenerate.')
      return
    }
    const noteInput = window.prompt('Optional note for AI regeneration (leave empty to skip):', '')
    const note = typeof noteInput === 'string' ? noteInput.trim() : ''
    setRegenLoading(true)
    setError('')
    try {
      const res = await documentationApi.regenerateChapter(doc._id, chapter._id, note ? { note } : {})
      if (res?.data?.data && onDocUpdate) onDocUpdate(res.data.data)
    } catch (e) {
      if (e?.response?.status === 402) {
        setError('Regeneration limit reached. Purchase more to regenerate.')
        return
      }
      setError(e?.response?.data?.message || 'Regenerate failed')
    } finally {
      setRegenLoading(false)
    }
  }

  const handleSaveEdit = async () => {
    setSaving(true)
    setError('')
    try {
      const res = await documentationApi.updateChapter(doc._id, chapter._id, aiInstruction ? { aiInstruction } : { content })
      if (res?.data?.data && onDocUpdate) onDocUpdate(res.data.data)
      const updatedChapter = res?.data?.data?.chapters?.find(c => c._id === chapter._id)
      if (updatedChapter) setContent(updatedChapter.content)
      setEditModal(false)
      setAiInstruction('')
    } catch (e) {
      if (e?.response?.status === 402) {
        setError('Edit limit reached. Purchase extra edits.')
        return
      }
      setError(e?.response?.data?.message || 'Update failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col w-full">
      <div className="sticky top-[4.5rem] z-30 -mx-2 mb-2 border-b border-slate-200/70 bg-white/95 px-2 py-2 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="flex justify-between items-start flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => setPage('Project')}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18" />
            </svg>
          </Button>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{chapter.title}</h2>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Button type="button" variant="outline" size="sm" onClick={() => navigate(`/dashboard/project/full/${doc._id}`)}>
            Full document
          </Button>
          <span className="text-sm text-slate-500">Edits: {editCount}/{maxEdits} · Regens: {regenCount}/3</span>
          <Button size="sm" disabled={!canRegen || regenLoading} loading={regenLoading} onClick={handleRegenerate}>
            Regenerate chapter
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!canEdit}
            onClick={() => {
              setContent(chapter.content)
              setEditModal(true)
            }}
          >
            Edit
          </Button>
        </div>
      </div>
      </div>
      {error && <p className="text-error text-sm mt-2">{error}</p>}
      <div className="mt-6 space-y-6">
        <Card className="shadow-soft">
          <CardBody className="whitespace-pre-wrap leading-relaxed text-slate-800">
            {renderContentWithDiagrams(content || chapter.content, chapter?.diagrams?.length ? chapter.diagrams : (chapter?.diagram ? [{ name: "Diagram", code: chapter.diagram }] : []))}
          </CardBody>
        </Card>
      </div>

      <dialog open={editModal} className="modal">
        <div className="modal-box max-w-2xl">
          <h2 className="text-xl font-bold text-slate-900">Edit chapter</h2>
          <div className="mt-4 space-y-4">
            <Field label="Content (or use AI instruction below)">
              <Textarea className="min-h-[220px]" value={content} onChange={e => setContent(e.target.value)} />
            </Field>
            <Field label="Or: Edit with AI (instruction)">
              <Input
                type="text"
                placeholder="e.g. Add a section on security"
                value={aiInstruction}
                onChange={e => setAiInstruction(e.target.value)}
              />
            </Field>
          </div>
          {error && <p className="text-error text-sm mt-2">{error}</p>}
          <div className="modal-action">
            <Button type="button" variant="outline" onClick={() => setEditModal(false)}>
              Cancel
            </Button>
            <Button type="button" loading={saving} onClick={handleSaveEdit}>
              Save
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop" onClick={() => setEditModal(false)}><button>close</button></form>
      </dialog>
    </div>
  )
}

export default SingleChapter
